import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { store } from '@/lib/states/store'
import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { VirtualizedDataTable } from '@/components/dataTable/VirtualizedDataTable'
import { ColumnDef } from '@tanstack/react-table'
import {
  CalendarDays,
  ClipboardList,
  Link2,
  ListOrdered,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react'
import LoadingSpinner from '@/components/LoadingSpinner'
import { errorToast } from '@/lib/utils/ToastUtils'
import { trpc } from '@/lib/trpc/client'
import { toTrainingSavePlanInput } from '@/lib/training/savePlanInput'
import { invalidateCoachOnboardingQueries } from '@/lib/utils/onboardingCache'
import { toast } from 'sonner'
import ClientSelectDialog, {
  LinkedClient,
} from '@/components/templates/ClientSelectDialog'
import { LinkedClientAvatarStack } from '@/components/templates/LinkedClientAvatarStack'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Dumbbell } from 'lucide-react'
import DeleteAlert from '@/components/DeleteAlert'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { requirePermission } from '@/lib/permissions/permissions'
import { queryClient } from '@/lib/utils/queryUtils'
import { useOrg } from '@/lib/states/session/hooks'
import {
  patchListCache,
  prependToList,
  removeFromList,
} from '@trainrr/api-client'

type TemplateRow = {
  id: string
  name: string
  org_id: string | null
  workout_count: number
  linked_clients: LinkedClient[]
  is_system: boolean
  typeLabel: 'Fixed' | 'Calendar'
}

export const Route = createFileRoute('/_auth/_dashboard/training/')({
  beforeLoad: () => {
    requirePermission('plans.write')
  },
  component: TrainingTemplateLibrary,
})

function TrainingTemplateLibrary() {
  const navigate = useNavigate()
  const org = useOrg()
  const utils = trpc.useUtils()
  const savePlan = trpc.training.savePlan.useMutation()
  const completeOnboardingItem = trpc.onboarding.completeItem.useMutation()
  const { data: templateRows, isPending: isTemplatesPending } =
    trpc.training.listTemplates.useQuery(undefined, {
      enabled: Boolean(org?.id),
    })
  const { data: clients } = trpc.training.listAssignableClients.useQuery(
    undefined,
    { enabled: Boolean(org?.id) },
  )

  const templates = useMemo<TemplateRow[]>(() => {
    return (templateRows ?? []).map((row) => ({
      id: row.id,
      name: row.name,
      org_id: row.org_id,
      workout_count: row.workoutCount,
      linked_clients: row.linkedClients.map((client) => ({
        id: client.id,
        first_name: client.first_name,
        last_name: client.last_name,
        avatar: client.avatar,
        avatar_path: client.avatar?.path ?? null,
      })),
      is_system: false,
      typeLabel: row.scheduling_mode === 'dated' ? 'Calendar' : 'Fixed',
    }))
  }, [templateRows])

  const [newDialogOpen, setNewDialogOpen] = useState(false)
  const [newTemplateName, setNewTemplateName] = useState('')
  const [newTemplateMode, setNewTemplateMode] = useState<'fixed' | 'dated'>(
    'fixed',
  )
  const [creatingTemplate, setCreatingTemplate] = useState(false)
  const [deleteTemplate, setDeleteTemplate] = useState<TemplateRow | null>(null)
  const [manageDialogOpen, setManageDialogOpen] = useState(false)
  const [manageDialogTemplateId, setManageDialogTemplateId] = useState<
    string | null
  >(null)
  const manageDialogTemplate =
    templates.find((template) => template.id === manageDialogTemplateId) ??
    null

  const deletePlan = trpc.training.deletePlan.useMutation({
    onSuccess: (_data, variables) => {
      patchListCache(utils.training.listTemplates, (old) =>
        removeFromList(old, variables.planId),
      )
    },
    onError: (error) => {
      console.error(error)
      errorToast(error)
    },
  })
  const setAssignments = trpc.training.setAssignments.useMutation({
    onSuccess: (result) => {
      patchListCache(utils.training.listTemplates, (old) =>
        old.map((row) =>
          row.id === result.planId
            ? { ...row, linkedClients: result.linkedClients }
            : row,
        ),
      )
    },
    onError: (error) => {
      console.error(error)
      errorToast(error)
    },
  })

  const handleCreateTemplate = async () => {
    if (creatingTemplate) return
    const orgId = store.getState().session.org?.id
    if (!orgId) return
    const trimmed = newTemplateName.trim()
    if (!trimmed) {
      toast.error('Please enter a template name.')
      return
    }

    setCreatingTemplate(true)
    const createParams = {
      org_id: orgId,
      workouts: [],
      deleted_workouts: [],
      planname: trimmed,
      p_client_id: null,
      planid: null,
      is_primary: false,
      is_visible: true,
      p_is_template: true,
      p_scheduling_mode: newTemplateMode,
      p_schedule_entries: [],
      p_fixed_schedule_entries: [],
    } as Record<string, unknown>

    let createdPlanId: string | undefined
    try {
      const saved = await savePlan.mutateAsync(
        toTrainingSavePlanInput(createParams),
      )
      createdPlanId = saved.id
    } catch (error) {
      console.error(error)
      errorToast(error instanceof Error ? error : null)
      setCreatingTemplate(false)
      return
    }
    if (!createdPlanId) {
      errorToast(null, 'Failed to create template')
      setCreatingTemplate(false)
      return
    }

    patchListCache(utils.training.listTemplates, (old) =>
      prependToList(old, {
        id: createdPlanId,
        name: trimmed,
        org_id: orgId,
        is_template: true,
        scheduling_mode: newTemplateMode,
        workoutCount: 0,
        linkedClients: [],
      }),
    )

    setNewDialogOpen(false)
    setNewTemplateName('')
    setNewTemplateMode('fixed')
    setCreatingTemplate(false)
    try {
      await completeOnboardingItem.mutateAsync({
        key: 'coach_onboarding',
        version: 1,
        itemKey: 'setup.program.create',
        source: 'auto',
        metadata: {
          source: 'training-template-create',
          plan_id: createdPlanId,
        },
      })
      await invalidateCoachOnboardingQueries(queryClient)
    } catch {
      // Onboarding completion is best-effort.
    }
    navigate({
      to: '/training/$templateId',
      params: { templateId: createdPlanId },
    })
  }

  const handleSaveAssignments = async (
    clientIds: string[],
    options?: { startDate?: string },
  ) => {
    if (!manageDialogTemplate) return false
    try {
      await setAssignments.mutateAsync({
        planId: manageDialogTemplate.id,
        clientIds,
        startDate: options?.startDate,
      })
    } catch {
      return false
    }
    toast.success('Template assignments updated')
    return true
  }

  const handleDeleteTemplate = async (template: TemplateRow) => {
    try {
      await deletePlan.mutateAsync({ planId: template.id })
    } catch {
      return
    }
    toast.success('Template deleted')
  }

  const handleConfirmDelete = async () => {
    if (!deleteTemplate) return
    const template = deleteTemplate
    setDeleteTemplate(null)
    await handleDeleteTemplate(template)
  }

  const columns = useMemo<ColumnDef<TemplateRow>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Template',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="font-medium">{row.original.name}</span>
            {row.original.is_system && (
              <Badge variant="secondary">Trainrr</Badge>
            )}
          </div>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        cell: ({ row }) => {
          const isCalendar = row.original.typeLabel === 'Calendar'
          return (
            <div className="flex items-center gap-2">
              {isCalendar ? (
                <CalendarDays className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ListOrdered className="h-4 w-4 text-muted-foreground" />
              )}
              <span>{row.original.typeLabel}</span>
            </div>
          )
        },
      },
      {
        id: 'workouts',
        header: 'Workouts',
        cell: ({ row }) => row.original.workout_count,
      },
      {
        id: 'linked',
        header: 'Linked Clients',
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={(event) => {
              event.stopPropagation()
              setManageDialogTemplateId(row.original.id)
              setManageDialogOpen(true)
            }}
          >
            <LinkedClientAvatarStack clients={row.original.linked_clients} />
          </Button>
        ),
      },
      {
        id: 'actions',
        cell: ({ row }) => (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon" variant="ghost">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    setManageDialogTemplateId(row.original.id)
                    setManageDialogOpen(true)
                  }}
                >
                  <Link2 className="mr-2 h-4 w-4" /> Link clients
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    navigate({
                      to: '/training/$templateId',
                      params: { templateId: row.original.id },
                    })
                  }
                  disabled={row.original.is_system}
                >
                  <Pencil className="mr-2 h-4 w-4" /> Edit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setDeleteTemplate(row.original)}
                  disabled={row.original.is_system}
                  variant="destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    [navigate],
  )

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="flex h-full min-h-0 w-full flex-col md:flex">
        <div className="flex min-h-0 flex-1 flex-col gap-4 p-8 pt-6">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h2 className="text-3xl font-bold tracking-tight">
                Training Templates
              </h2>
              <p className="text-sm text-muted-foreground">
                Build reusable training plans and assign them to clients.
              </p>
            </div>
            <Button className="gap-2" onClick={() => setNewDialogOpen(true)}>
              <Plus className="h-4 w-4" /> New Template
            </Button>
          </div>
          <VirtualizedDataTable
            className="min-h-0 flex-1"
            columns={columns}
            data={templates}
            isLoading={isTemplatesPending}
            onRowSelected={(row) =>
              navigate({
                to: '/training/$templateId',
                params: { templateId: row.id },
              })
            }
            showSearchBar
            stickyHeader
            tableContainerClassName="min-h-0 flex-1"
            tableWrapperClassName="h-full min-h-0"
            virtualizedHeight="100%"
            estimateRowHeight={72}
            emptyView={
              <Empty className="border border-dashed">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Dumbbell className="h-5 w-5" />
                  </EmptyMedia>
                  <EmptyTitle>No training templates yet</EmptyTitle>
                  <EmptyDescription>
                    Create a reusable training plan to assign to clients.
                  </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                  <Button
                    className="gap-2"
                    onClick={() => setNewDialogOpen(true)}
                  >
                    <Plus className="h-4 w-4" /> New template
                  </Button>
                </EmptyContent>
              </Empty>
            }
          />
        </div>
      </div>

      <Dialog
        open={newDialogOpen}
        onOpenChange={(open) => {
          if (creatingTemplate) return
          setNewDialogOpen(open)
        }}
        analyticsName="training_new_template_dialog"
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New training template</DialogTitle>
            <DialogDescription>
              Choose how this template should be structured.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={newTemplateName}
            onChange={(event) => setNewTemplateName(event.target.value)}
            placeholder="Template name"
            disabled={creatingTemplate}
          />
          <div className="space-y-3">
            <button
              type="button"
              disabled={creatingTemplate}
              onClick={() => setNewTemplateMode('fixed')}
              className={`flex w-full items-start gap-3 rounded-2xl border px-4 py-3 text-left transition disabled:opacity-60 ${
                newTemplateMode === 'fixed'
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-border bg-muted/20 text-muted-foreground hover:bg-muted/40'
              }`}
            >
              <ClipboardList className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <div className="text-base font-semibold">Fixed</div>
                <div className="text-sm text-muted-foreground">
                  Repeating plan where clients can follow a consistent weekly
                  pattern.
                </div>
              </div>
            </button>
            <button
              type="button"
              disabled={creatingTemplate}
              onClick={() => setNewTemplateMode('dated')}
              className={`flex w-full items-start gap-3 rounded-2xl border px-4 py-3 text-left transition disabled:opacity-60 ${
                newTemplateMode === 'dated'
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-border bg-muted/20 text-muted-foreground hover:bg-muted/40'
              }`}
            >
              <CalendarDays className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <div className="text-base font-semibold">Calendar</div>
                <div className="text-sm text-muted-foreground">
                  Date-based plan with workouts scheduled on specific calendar
                  days.
                </div>
              </div>
            </button>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNewDialogOpen(false)}
              disabled={creatingTemplate}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateTemplate} disabled={creatingTemplate}>
              Create &amp; Edit
              {creatingTemplate && <LoadingSpinner className="ml-2" />}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ClientSelectDialog
        open={manageDialogOpen}
        onOpenChange={(open) => {
          setManageDialogOpen(open)
          if (!open) setManageDialogTemplateId(null)
        }}
        clients={clients ?? []}
        initialSelectedIds={
          manageDialogTemplate
            ? manageDialogTemplate.linked_clients.map((client) => client.id)
            : []
        }
        onSave={handleSaveAssignments}
        title={manageDialogTemplate?.name ?? 'Assign training template'}
        description="Link this plan to clients so that changes are synced to all."
        headerIcon={<Link2 className="h-4 w-4" />}
        saveLabel="Save"
        allowEmpty
        showStartDate={manageDialogTemplate?.typeLabel === 'Calendar'}
        startDateLabel="Start day"
      />
      <DeleteAlert
        title={`Delete ${deleteTemplate?.name ?? 'template'}?`}
        description={`Are you sure you want to delete ${
          deleteTemplate?.name ?? 'this template'
        }?`}
        open={Boolean(deleteTemplate)}
        onCancel={() => setDeleteTemplate(null)}
        onDelete={handleConfirmDelete}
      />
    </div>
  )
}
